import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { cn } from "../lib/utils";

export interface IssueListProps {
  issues: string[];
  className?: string;
}

/**
 * Render the Worker-returned `issues` strings. Severity is inferred from
 * common Chinese cue words; falls back to neutral.
 */
export function IssueList({ issues, className }: IssueListProps) {
  if (issues.length === 0) {
    return (
      <div className={cn("flex items-center gap-2 text-sm text-[color:var(--good)]", className)}>
        <CheckCircle2 className="h-4 w-4" />
        <span>未发现明显问题，继续保持！</span>
      </div>
    );
  }

  return (
    <ul className={cn("flex flex-col gap-2", className)}>
      {issues.map((issue, idx) => {
        const severity = classifySeverity(issue);
        return (
          <li
            key={`${idx}-${issue}`}
            className={cn(
              "flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
              severity === "warn" &&
                "border-[color:var(--warn)]/30 bg-[color:var(--warn)]/5 text-[color:var(--warn)]",
              severity === "accent" &&
                "border-[color:var(--accent)]/30 bg-[color:var(--accent)]/5 text-[color:var(--accent)]",
              severity === "neutral" &&
                "border-[color:var(--border)] bg-[color:var(--surface-2)] text-[color:var(--text)]",
            )}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{issue}</span>
          </li>
        );
      })}
    </ul>
  );
}

function classifySeverity(text: string): "warn" | "accent" | "neutral" {
  const t = text.toLowerCase();
  // Hard failures (笔画缺失, 比例失准, 严重) → red/accent
  if (/(缺失|失准|严重|错误|不完整|遗漏)/.test(t)) return "accent";
  // Soft hints (略, 稍, 注意) → orange/warn
  if (/(略|稍|注意|微|不够|偏|歪|不够)/.test(t)) return "warn";
  return "neutral";
}
